import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { COMBAT_SKILLS, ENEMIES, type CombatSkill, type Enemy } from '@/data/combat';
import { Sword, Shield, Heart } from 'lucide-react';

interface CombatScreenProps {
  onClose: () => void;
}

export const CombatScreen: React.FC<CombatScreenProps> = ({ onClose }) => {
  const [playerHealth, setPlayerHealth] = useState(100);
  const [playerMana, setPlayerMana] = useState(50);
  const [playerMaxHealth] = useState(100);
  const [playerMaxMana] = useState(50);
  
  const [enemy, setEnemy] = useState<Enemy>(ENEMIES[0]);
  const [enemyHealth, setEnemyHealth] = useState(ENEMIES[0].maxHealth);
  const [battleLog, setBattleLog] = useState<string[]>(['Battle started!']);
  const [gameOver, setGameOver] = useState(false);
  const [victory, setVictory] = useState(false);

  const addLog = (message: string) => {
    setBattleLog((prev) => [...prev, message]);
  };

  const handleSkillUse = (skill: CombatSkill) => {
    // Check if player has enough mana
    if (playerMana < skill.manaCost) {
      addLog(`Not enough mana for ${skill.name}!`);
      return;
    }

    // Apply skill
    setPlayerMana(Math.max(0, playerMana - skill.manaCost));

    // Calculate damage if skill deals damage
    if (skill.type === 'physical' || skill.type === 'magical') {
      const damage = Math.floor(
        Math.random() * (skill.damageMax - skill.damageMin + 1) + skill.damageMin
      );
      setEnemyHealth(Math.max(0, enemyHealth - damage));
      addLog(`You used ${skill.name}! Dealt ${damage} damage.`);

      // Check if enemy is defeated
      if (enemyHealth - damage <= 0) {
        setVictory(true);
        addLog(`${enemy.name} has been defeated! You gained ${enemy.experience} XP and ${enemy.gold} gold!`);
        return;
      }
    } else if (skill.type === 'support') {
      if (skill.effect === 'restore-health') {
        const heal = Math.floor(Math.random() * 20 + 20);
        setPlayerHealth(Math.min(playerMaxHealth, playerHealth + heal));
        addLog(`You used ${skill.name}! Restored ${heal} health.`);
      }
    }

    // Enemy turn
    setTimeout(() => {
      const enemyDamage = Math.floor(Math.random() * (enemy.attack + 5 - enemy.attack + 1) + enemy.attack);
      setPlayerHealth(Math.max(0, playerHealth - enemyDamage));
      addLog(`${enemy.name} attacks! Dealt ${enemyDamage} damage.`);

      if (playerHealth - enemyDamage <= 0) {
        setGameOver(true);
        addLog('You have been defeated...');
      }
    }, 1000);
  };

  const playerHealthPercent = (playerHealth / playerMaxHealth) * 100;
  const enemyHealthPercent = (enemyHealth / enemy.maxHealth) * 100;

  return (
    <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl bg-gradient-to-br from-slate-900 to-black border-red-600 p-8 space-y-6">
        {/* Battle Title */}
        <div className="text-center border-b border-red-600 pb-4">
          <h2 className="text-4xl font-bold text-red-500 mb-2">Battle!</h2>
          <p className="text-gray-300">Defeat the enemy to claim victory!</p>
        </div>

        <div className="grid grid-cols-2 gap-8">
          {/* Player Section */}
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-xl font-bold text-orange-400 mb-2">You</h3>
              <Sword className="w-12 h-12 text-orange-500 mx-auto mb-2" />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Health</span>
                <span className="text-red-400 font-bold">
                  {Math.max(0, playerHealth)} / {playerMaxHealth}
                </span>
              </div>
              <Progress value={playerHealthPercent} className="h-4" />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Mana</span>
                <span className="text-blue-400 font-bold">
                  {playerMana} / {playerMaxMana}
                </span>
              </div>
              <Progress value={(playerMana / playerMaxMana) * 100} className="h-4 bg-blue-900" />
            </div>
          </div>

          {/* Enemy Section */}
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-xl font-bold text-red-400 mb-2">{enemy.name}</h3>
              <Shield className="w-12 h-12 text-red-500 mx-auto mb-2 rotate-45" />
              <p className="text-gray-400 text-sm">Level {enemy.level}</p>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Health</span>
                <span className="text-red-400 font-bold">
                  {Math.max(0, enemyHealth)} / {enemy.maxHealth}
                </span>
              </div>
              <Progress value={enemyHealthPercent} className="h-4" />
            </div>

            <div className="bg-slate-800 p-2 rounded text-center">
              <p className="text-gray-400 text-xs">ATK: {enemy.attack} | DEF: {enemy.defense}</p>
            </div>
          </div>
        </div>

        {/* Battle Log */}
        <div className="bg-slate-900 border border-gray-700 rounded p-4 h-32 overflow-y-auto space-y-1">
          {battleLog.map((log, idx) => (
            <p key={idx} className="text-gray-300 text-sm font-mono">
              {log}
            </p>
          ))}
        </div>

        {/* Skills */}
        {!gameOver && !victory && (
          <div className="space-y-3">
            <p className="text-orange-400 font-semibold">Choose an action:</p>
            <div className="grid grid-cols-2 gap-2">
              {COMBAT_SKILLS.slice(0, 4).map((skill) => (
                <Button
                  key={skill.id}
                  onClick={() => handleSkillUse(skill)}
                  disabled={playerMana < skill.manaCost}
                  className="flex flex-col items-start justify-start h-auto py-2 px-3 bg-slate-700 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white border border-gray-600 hover:border-orange-600 transition"
                >
                  <span className="font-semibold text-sm">{skill.name}</span>
                  <span className="text-xs text-gray-300">Mana: {skill.manaCost}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Victory/Defeat Screen */}
        {(gameOver || victory) && (
          <div className="bg-slate-800 border border-orange-500 rounded p-6 text-center space-y-4">
            <h3 className={`text-2xl font-bold ${victory ? 'text-green-400' : 'text-red-400'}`}>
              {victory ? 'Victory!' : 'Defeated...'}
            </h3>
            <div className="space-y-2 text-gray-300">
              {victory && (
                <>
                  <p>You have emerged victorious!</p>
                  <div className="bg-slate-700 p-3 rounded">
                    <p className="text-yellow-400">+{enemy.experience} Experience</p>
                    <p className="text-yellow-400">+{enemy.gold} Gold</p>
                  </div>
                </>
              )}
              {gameOver && <p>You need to recover before your next battle...</p>}
            </div>
            <Button
              onClick={onClose}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold"
            >
              {victory ? 'Continue' : 'Return to World'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};
